import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, ArrowLeft, Loader2 } from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { ShareButtons } from "@/components/landing/ShareButtons";
import { TrustBadges } from "@/components/landing/TrustBadges";
import { PreOrderForm } from "@/components/landing/PreOrderForm";
import { AromaGallery } from "@/components/landing/AromaGallery";
import { ConversionBlock } from "@/components/landing/ConversionBlock";
import { StickyBar } from "@/components/landing/StickyBar";
import { LeadPopup } from "@/components/landing/LeadPopup";
import { trackProductClick } from "@/utils/tracking";
import { setPageMeta } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const src = searchParams.get("src") || "direct";
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading");
  const [buying, setBuying] = useState(false);
  const [waNumber, setWaNumber] = useState("");

  useEffect(() => {
    axios.get(`${API}/contact`).then((r) => setWaNumber(r.data.wa_number || "")).catch(() => {});
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/products/${slug}`)
      .then((res) => {
        setProduct(res.data);
        setStatus("ok");
        setPageMeta(
          `${res.data.name} — NALURA`,
          `${res.data.tagline || ""} ${res.data.description || ""}`.trim().slice(0, 155)
        );
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const handleBuy = async (lokasi = "detail-atas") => {
    setBuying(true);
    trackProductClick(product.name, lokasi);
    try {
      const res = await axios.post(`${API}/products/${product.product_id}/click`, { source: src });
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(product.affiliate_url, "_blank", "noopener,noreferrer");
    } finally {
      setBuying(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#FBF7EE] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#A67C1E] flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat produk...
        </p>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-[#FBF7EE] text-[#211B10] flex flex-col items-center justify-center gap-6 px-6" data-testid="product-not-found">
        <p className="font-display text-4xl tracking-tight">Produk tidak ditemukan</p>
        <Link to="/" data-testid="back-to-home-link" className="inline-flex items-center gap-2 text-[#A67C1E] font-mono text-xs uppercase tracking-[0.25em] hover:text-[#C9A227] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF7EE] text-[#211B10] min-h-screen pb-16 md:pb-0" data-testid="product-detail-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.tagline || product.description,
            image: product.image_url,
            brand: { "@type": "Brand", name: "NALURA" },
            url: `${window.location.origin}/p/${product.slug || product.product_id}`,
          }).replace(/</g, "\\u003c"),
        }}
      />
      <Nav />

      <main className="pt-28 md:pt-36 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/" data-testid="detail-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/60 hover:text-[#A67C1E] transition-colors mb-12">
            <ArrowLeft className="w-3.5 h-3.5" /> Semua Koleksi
          </Link>

          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden border border-[#211B10]/10"
            >
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full aspect-[4/3] object-cover"
                data-testid="detail-product-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" aria-hidden="true" />
              <span className={`absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.25em] backdrop-blur px-3 py-1.5 ${product.coming_soon ? "text-[#211B10] border border-[#211B10]/20 bg-[#FBF7EE]/90" : "text-[#EACB6B] border border-[#EACB6B]/40 bg-[#211B10]/85"}`}>
                {product.coming_soon ? "Segera Hadir" : "Pilihan Kurator"}
              </span>
            </motion.div>

            <div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                <h1 data-testid="detail-product-name" className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
                  {product.name}
                </h1>
                <p data-testid="detail-product-tagline" className="text-[#A67C1E] font-display italic text-xl md:text-2xl mb-8">
                  {product.tagline}
                </p>
                <p data-testid="detail-product-description" className="text-base text-[#211B10]/65 leading-relaxed mb-10">
                  {product.description}
                </p>
              </motion.div>

              {product.benefits?.length > 0 && (
                <motion.ul
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.35 }}
                  className="space-y-4 mb-12"
                  data-testid="detail-product-benefits"
                >
                  {product.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-[#211B10]/75">
                      <Check className="w-4 h-4 text-[#A67C1E] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </motion.ul>
              )}

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                {product.coming_soon ? (
                  <PreOrderForm product={product} waNumber={waNumber} />
                ) : (
                  <>
                    <div className="flex items-center gap-6 flex-wrap mb-6">
                      {product.price_display && (
                        <span data-testid="detail-product-price" className="font-display text-3xl">{product.price_display}</span>
                      )}
                      <button
                        data-testid={`detail-buy-btn-${product.product_id}`}
                        onClick={() => handleBuy("detail-atas")}
                        disabled={buying}
                        className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-sm uppercase tracking-[0.12em] px-8 py-4 hover:bg-[#211B10] hover:-translate-y-1 transition-[background-color,transform] duration-300 disabled:opacity-60 disabled:translate-y-0"
                      >
                        {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                        {product.cta_text || "Dapatkan Sekarang"}
                      </button>
                    </div>

                    <div className="mb-10">
                      <TrustBadges />
                    </div>
                  </>
                )}

                <div className="border-t border-[#211B10]/10 pt-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/60 mb-4">Bagikan produk ini</p>
                  <ShareButtons product={product} size="lg" story />
                </div>
              </motion.div>
            </div>
          </div>

          <Reveal className="mt-24 md:mt-32 border border-[#211B10]/10 bg-white p-10 md:p-16 max-w-4xl">
            {product.coming_soon ? (
              <>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Ritual yang Sempurna</p>
                <p className="font-display text-2xl md:text-3xl tracking-tight leading-snug text-[#211B10]/90">
                  Ruangan dan aroma adalah perpaduan yang luar biasa. Saat keduanya selaras,
                  pikiran lebih mudah rileks — dan alam bawah sadar terbuka lebih cepat dan maksimal.
                </p>
                <p className="mt-6 text-sm text-[#211B10]/60 leading-relaxed max-w-2xl">
                  Pesan sekarang lewat Open Pre-Order — parfum Anda kami siapkan dalam 2–4 minggu,
                  tersedia dari 10ml hingga 250ml untuk ruangan luas.
                </p>
              </>
            ) : (
              <>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Mengapa Sekarang</p>
                <p className="font-display text-2xl md:text-3xl tracking-tight leading-snug text-[#211B10]/90">
                  Setiap hari yang lewat dengan pola pikir lama adalah satu peluang yang tidak kembali.
                  Pertanyaannya bukan apakah Anda mampu — melainkan kapan Anda memutuskan untuk mulai.
                </p>
                <button
                  data-testid="detail-bottom-cta"
                  onClick={() => handleBuy("detail-bawah")}
                  disabled={buying}
                  className="mt-10 inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-sm uppercase tracking-[0.12em] px-8 py-4 hover:bg-[#211B10] hover:-translate-y-1 transition-[background-color,transform] duration-300 disabled:opacity-60"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  {product.cta_text || "Dapatkan Sekarang"}
                </button>
              </>
            )}
          </Reveal>

          {product.coming_soon && <AromaGallery />}

          {!product.coming_soon && <ConversionBlock product={product} />}
        </div>
      </main>

      <Footer />
      {!product.coming_soon && <StickyBar product={product} onBuy={handleBuy} buying={buying} />}
      <LeadPopup enableTimer />
    </div>
  );
}
